import { apiDatetimeToDate } from "@warrenio/api-spec/conversion";
import { useDateFormatter, type DateFormatterOptions } from "react-aria";

/** For reference */
interface __DateFormatterOptions {
    calendar?: string;
    localeMatcher?: "best fit" | "lookup";
    weekday?: "short" | "long" | "narrow";
    era?: "short" | "long" | "narrow";
    year?: "numeric" | "2-digit";
    month?: "short" | "long" | "numeric" | "2-digit" | "narrow";
    day?: "numeric" | "2-digit";
    hour?: "numeric" | "2-digit";
    minute?: "numeric" | "2-digit";
    second?: "numeric" | "2-digit";
    timeZoneName?: "short" | "long" | "shortOffset" | "longOffset" | "shortGeneric" | "longGeneric";
    formatMatcher?: "basic" | "best fit";
    hour12?: boolean;
    timeZone?: string;
    dayPeriod?: "short" | "long" | "narrow";
    numberingSystem?: string;
    dateStyle?: "short" | "long" | "full" | "medium";
    timeStyle?: "short" | "long" | "full" | "medium";
    hourCycle?: "h11" | "h12" | "h23" | "h24";
    fractionalSecondDigits?: 1 | 2 | 3;
}

export type DateLike = string | Date;

function toDate(date: DateLike) {
    return date instanceof Date ? date : apiDatetimeToDate(date);
}

export interface DateProps {
    date: DateLike;
    forceUtc?: boolean;
}

function useFormattedDate(options: DateFormatterOptions, { date, forceUtc }: DateProps) {
    const formatter = useDateFormatter({ ...options, timeZone: forceUtc ? "UTC" : undefined });
    return formatter.format(toDate(date));
}

const longOptions: DateFormatterOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "shortGeneric",
};

export function LongDate(props: DateProps) {
    return useFormattedDate(longOptions, props);
}

const shortOptions: DateFormatterOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
};

export function ShortDate(props: DateProps) {
    return useFormattedDate(shortOptions, props);
}

const compactDateTimeOptions: DateFormatterOptions = {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
};

// TODO: Use this somewhere?
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function CompactDateTime(props: DateProps) {
    return useFormattedDate(compactDateTimeOptions, props);
}

const monthYearOptions: DateFormatterOptions = {
    year: "numeric",
    month: "short",
};

export function MonthYearDate(props: DateProps) {
    return useFormattedDate(monthYearOptions, props);
}

const monthLongYearOptions: DateFormatterOptions = {
    year: "numeric",
    month: "long",
};

export function MonthLongYearDate(props: DateProps) {
    return useFormattedDate(monthLongYearOptions, props);
}

const timeTimezoneOptions: DateFormatterOptions = {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "shortGeneric",
};

export function AdminDate(props: DateProps) {
    const date = useFormattedDate(shortOptions, props);
    const time = useFormattedDate(timeTimezoneOptions, props);
    return (
        <div className="text-nowrap">
            {date}
            <div className="font-size-small color-muted">{time}</div>
        </div>
    );
}
